import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import LicenseProductSelect from "@/v2-backoffice-client/license/LicenseProductSelect"
import LicenseSeatSourceSelect from "@/v2-backoffice-client/license/plan/LicenseSeatSourceSelect"
import { useLicensePlanUpdateMutation } from "@/v2-backoffice-client/license/plan/update/license.plan.update.mutation"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

// Radix Select items can't carry an empty-string value, so "not set" gets a
// sentinel that is translated to null on submit.
const NOT_SET_VALUE = "0"

export type LicensePlanUpdateDialogProps = {
  plan: LicensePlanFragment
  open: boolean
  onOpenChange(open: boolean): void
}

// Update License: the two things we own on a Fenerum-mirrored license, which
// product it is for and how its subscription quantities count seats. These
// are the only two editable columns — everything else on the row is
// Fenerum's and rewritten by the sync. Triggered from the board's row menu
// and the license panel's toolbar.
function LicensePlanUpdateDialog(props: LicensePlanUpdateDialogProps) {
  const { plan, open, onOpenChange } = props
  const [productCode, setProductCode] = useState(
    plan.ProductCode !== null && plan.ProductCode !== undefined
      ? String(plan.ProductCode)
      : NOT_SET_VALUE,
  )
  const [seatSourceCode, setSeatSourceCode] = useState(
    plan.SeatSourceCode !== null && plan.SeatSourceCode !== undefined
      ? String(plan.SeatSourceCode)
      : NOT_SET_VALUE,
  )

  // The mutation returns the LicensePlanFragment, so Apollo updates the
  // normalized row in place — no evict needed.
  const [updatePlan, { loading, error }] = useLicensePlanUpdateMutation({
    onCompleted(data) {
      const updated = data.updateLicensePlan
      toast({
        variant: "success",
        title: "License updated",
        description: updated.ProductName
          ? `${updated.Name} is now for ${updated.ProductName}.`
          : `${updated.Name} has no product yet, so the import skips it.`,
      })
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update License — {plan.Name}</Dialog.Title>
          <Dialog.Description>
            Fenerum owns everything else about this license; these two fields
            are ours. Without a product the import skips this license&apos;s
            subscriptions, so no customer is put on it and the board shows no
            customers or MRR for it. Without seat semantics its customers get no
            seat count.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Product</Label>
            <LicenseProductSelect
              value={productCode}
              onValueChange={setProductCode}
            >
              <Select.Item
                value={NOT_SET_VALUE}
                description="Subscriptions of this license give the customer nothing until a product is picked."
              >
                No product
              </Select.Item>
            </LicenseProductSelect>
            <p className="text-xs text-muted-foreground">
              Which product this license is for. The license&apos;s packages
              then unlock features for that customer.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Seat semantics</Label>
            <LicenseSeatSourceSelect
              value={seatSourceCode}
              onValueChange={setSeatSourceCode}
            >
              <Select.Item
                value={NOT_SET_VALUE}
                description="Customers on this license get no seat count (uncapped) until the semantics are declared."
              >
                Not declared
              </Select.Item>
            </LicenseSeatSourceSelect>
            <p className="text-xs text-muted-foreground">
              Where the customer&apos;s seat count comes from. Fenerum has no
              seat field, only a quantity per subscription, and what that
              quantity counts differs per license. The count becomes the cap the
              Seats badge is held against on the customer and the Licensing tab;
              it is reported, never enforced.
            </p>
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            data-feature-code="UI_BackofficeLicensePlan_Mapping_Save"
            onClick={() => {
              updatePlan({
                variables: {
                  id: plan.Id,
                  input: {
                    ProductCode:
                      productCode === NOT_SET_VALUE
                        ? null
                        : Number(productCode),
                    SeatSourceCode:
                      seatSourceCode === NOT_SET_VALUE
                        ? null
                        : Number(seatSourceCode),
                  },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicensePlanUpdateDialog
